import { ReactNode } from 'react'
import { AutocompleteOptionType, IOptionType } from '../../interfaces/entityInfo'
import { IFilterTreeGroup } from './filterTree'

export enum FilterFieldType {
  Autocomplete = 'autocomplete',
  List = 'list',
  Amount = 'amount',
  Percent = 'percent',
  Number = 'number',
  Text = 'text',
  Date = 'date',
  Datetime = 'datetime',
  Empty = 'empty',
  FullSearch = 'fullSearch',
  QuickFilter = 'quickFilter',
  Boolean = 'boolean',
}

export interface IQuickFilter {
  title: string
  filters: Record<string, unknown>
  isHidden?: boolean
  // Set on a saved filter: applying it replaces the table's filter tree instead of its flat filters.
  filterTree?: IFilterTreeGroup
}

export interface IFilter {
  field: string
  dbField?: string
  type: FilterFieldType
  // A text field the AND/OR filter editor offers is/contains/is not/doesn't contain for.
  hasTextMatch?: boolean
  loadOptions?: (value: string) => Promise<any[]>
  alias?: string
  isCustom?: boolean
  isMultiple?: boolean
  isSortable?: boolean
  isFullWidth?: boolean
  isRawText?: boolean
  placeholder?: string
  title?: string
  /** Explains the column; rendered as a help icon beside the header. */
  description?: string
  filterTitle?: string
  /**
   * The column header on the dense mobile table, where a cell fits ~13 characters.
   *
   * Optional: `abbreviateTitle` derives a label from `title` for every column, so this is
   * only for the cases where the derived one reads badly. The full title stays in the
   * header tooltip either way.
   */
  shortTitle?: string
  noteColumnId?: string
  align?: 'left' | 'center' | 'right'
  // Floor this column may be dragged to in a resizable table, when the uniform
  // default is wrong for it — an icon-only or date column is narrower than the
  // default allows, and useResizableColumns re-applies the floor on every load.
  minWidth?: number
  // Pre-resize width in a resizable table (e.g. '5.2%'). Omit to take the
  // remainder; follows the column wherever it is moved. No calc() mixing % and
  // px — fixed-layout cells drop it and every column falls back to `auto`.
  defaultWidth?: string
  options?: AutocompleteOptionType[] | { value: string; label: string }[] | IOptionType[]
  isHighlighted?: boolean
  highlightColor?: string
  renderTitle?: ReactNode
  excludeFilter?: boolean
  excludeFullSearchFilter?: boolean
  excludeColumn?: boolean
  isExternalField?: boolean
  aggColumn?: boolean
  fields?: readonly {
    field: string
    type: FilterFieldType
    isMultiple?: boolean
  }[]
  quickFilters?: readonly IQuickFilter[]
  render?: (row: any, index: number) => ReactNode
  mobileRender?: 'mainText' | 'secondaryText' | 'primaryNumber' | 'secondaryNumber' | 'icon'
}

// Sentinel value for list/autocomplete filters meaning "no value assigned" (NULL in the DB).
// The server filter builders (buildFiltersQuery / buildRawFiltersQuery) translate this value
// into an `IS NULL` condition. Kept non-empty so it survives URL/query serialization round-trips.
export const UNMAPPED_FILTER_VALUE = '__unmapped__'

export const UNMAPPED_FILTER_OPTION = { value: UNMAPPED_FILTER_VALUE, label: 'Unmapped' }

export const PER_PAGE = 40

export const FULLSCREEN_PER_PAGE = 60

export const SHORT_FILTERS_COUNT = 3

export const SHORT_FILTERS_SORTING_TYPES = ['amount', 'percent', 'number']

export const getApplicableFilters = (filters: IFilter[]): IFilter[] =>
  filters?.filter((filter) => !filter.excludeColumn && filter.type !== 'quickFilter') || []

export const EMPTY_ORDER_BY: { field: string | null; direction: 'ASC' | 'DESC' } = {
  field: null,
  direction: 'ASC',
}

// ============================================================================
// Type-Safe Filter Generation
// ============================================================================

/**
 * Extract field literal type from a filter config item
 * This uses a trick to extract the actual literal value, not just 'string'
 */
type ExtractField<T> = T extends { field: infer F extends string } ? F : never

/**
 * Extract fields from quickFilter nested fields
 */
type ExtractQuickFilterFields<T> = T extends { fields: readonly (infer F)[] }
  ? F extends { field: infer FF extends string }
    ? FF
    : never
  : never

/**
 * Extract all non-excluded field names from filter config
 */
type ExtractFilterFields<T extends readonly IFilter[]> = {
  [K in keyof T]: T[K] extends { excludeFilter: true }
    ? never
    : T[K] extends { type: FilterFieldType.Empty | FilterFieldType.FullSearch }
      ? never
      : T[K] extends { type: FilterFieldType.QuickFilter }
        ? ExtractQuickFilterFields<T[K]>
        : T[K] extends {
              type:
                | FilterFieldType.Amount
                | FilterFieldType.Percent
                | FilterFieldType.Number
                | FilterFieldType.Date
                | FilterFieldType.Datetime
            }
          ? never
          : ExtractField<T[K]>
}[number]

/**
 * Helper type to determine the value type for a specific field based on isMultiple
 */
type FilterFieldValue<T extends readonly IFilter[], F extends string> = {
  [K in keyof T]: T[K] extends { field: F; isMultiple: true }
    ? string[]
    : T[K] extends { field: F }
      ? string
      : never
}[number]

/**
 * Extract field names that need Min/Max suffix (numeric types)
 */
type ExtractRangeFields<T extends readonly IFilter[]> = {
  [K in keyof T]: T[K] extends { excludeFilter: true }
    ? never
    : T[K] extends {
          type: FilterFieldType.Amount | FilterFieldType.Percent | FilterFieldType.Number
        }
      ? ExtractField<T[K]>
      : never
}[number]

/**
 * Extract field names that need From/To suffix (date types)
 */
type ExtractDateFields<T extends readonly IFilter[]> = {
  [K in keyof T]: T[K] extends { excludeFilter: true }
    ? never
    : T[K] extends { type: FilterFieldType.Date | FilterFieldType.Datetime }
      ? ExtractField<T[K]>
      : never
}[number]

/**
 * Generates a complete filter type from a filter configuration array
 * Mirrors the logic in Base.buildFiltersValidationRules
 *
 * Non-range/non-date fields are typed as string[] if isMultiple: true, otherwise string.
 * This provides accurate type safety based on the filter configuration.
 *
 * @example
 * ```typescript
 * type MyFilters = FiltersFromConfig<typeof MY_FILTERS_CONFIG>
 * // Results in: { fullSearch?: string, field1?: string, field2?: string[], field3Min?: number, ... }
 * ```
 */
export type FiltersFromConfig<T extends readonly IFilter[]> = {
  fullSearch?: string
  [key: `${string}_or`]: string | string[] | undefined
} & {
  [K in ExtractFilterFields<T>]?: FilterFieldValue<T, K>
} & {
  [K in ExtractRangeFields<T> as `${K}Min`]?: number
} & {
  [K in ExtractRangeFields<T> as `${K}Max`]?: number
} & {
  [K in ExtractDateFields<T> as `${K}From`]?: string
} & {
  [K in ExtractDateFields<T> as `${K}To`]?: string
}
